import { useMemo } from 'react'
import { collection, deleteDoc, doc, serverTimestamp, setDoc } from 'firebase/firestore'
import { useCollection } from 'react-firebase-hooks/firestore'
import { firebase_firestore } from '../../../common/firebaseConfig'
import { useApplicationSuiteContext } from '../../../contexts/ApplicationSuiteProvider'
import StandardAlertTemplate from '../types/StandardAlertTemplate'

function useTemplatesCollection() {
  const { tradingHouse } = useApplicationSuiteContext()

  return useMemo(
    () => collection(firebase_firestore, `tradingHouses/${tradingHouse}/standardAlertTemplates`),
    [tradingHouse]
  )
}

// Writes only, deliberately without a snapshot listener: the edit panel never reads the
// collection, and subscribing there would put a second live listener on it alongside the
// table's for as long as the panel is open.
export function useStandardAlertTemplateMutations() {
  const { tradingHouse, usermeta } = useApplicationSuiteContext()

  const templatesCollection = useTemplatesCollection()

  const saveTemplate = async (
    templateId: string | undefined,
    data: Omit<StandardAlertTemplate, 'id' | 'updatedAt' | 'updatedBy'>
  ): Promise<string> => {
    const docRef = templateId
      ? doc(
          firebase_firestore,
          `tradingHouses/${tradingHouse}/standardAlertTemplates/${templateId}`
        )
      : doc(templatesCollection)

    // Full replace, not a merge — intentional. Template docs are client-owned settings
    // records; nothing else (the provisioning or sync triggers in firebase-functions)
    // ever writes to a template doc itself, only reads from it, so there's no
    // backend-owned field here that a full overwrite could wipe.
    await setDoc(docRef, {
      ...data,
      id: docRef.id,
      updatedAt: serverTimestamp(),
      updatedBy: usermeta?.gcid,
    })

    return docRef.id
  }

  const deleteTemplate = async (templateId: string): Promise<void> => {
    await deleteDoc(
      doc(firebase_firestore, `tradingHouses/${tradingHouse}/standardAlertTemplates/${templateId}`)
    )
  }

  return { saveTemplate, deleteTemplate }
}

/**
 * `enabled: false` skips the subscription entirely rather than just ignoring its result.
 * Every tab mounts at app load and two of them mount an AlertProvider, so an ungated
 * listener here would open for every signed-in user — including ones who can never be
 * shown a template — and stay open all session.
 */
export function useStandardAlertTemplates({ enabled = true }: { enabled?: boolean } = {}) {
  const templatesCollection = useTemplatesCollection()
  const mutations = useStandardAlertTemplateMutations()

  const [snapshot, loading, error] = useCollection(
    enabled ? (templatesCollection as any) : undefined
  )

  const templates = useMemo(
    () =>
      snapshot?.docs.map((d) => ({ ...d.data(), id: d.id })) as StandardAlertTemplate[] | undefined,
    [snapshot]
  )

  // `loaded` is whether a snapshot has arrived: `loading` still reads false for the render in
  // which a newly enabled subscription starts.
  return {
    templates: templates || [],
    loading,
    loaded: snapshot !== undefined,
    error,
    ...mutations,
  }
}
